import React, {useCallback, useEffect, useState} from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/rootReducer';
import { ApplicationDispatch } from '../../store/application/types';
import {
  Button,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  MenuItem, IconButton
} from "@mui/material";
import {IEmployee} from "../../Interfaces/Admin/IEmployee";
import {
  canViewChecklist,
  getFinishedChecklistItemsString,
  getNumChecklistItemsFinished,
  getUnexcludedApplicationChecklists
} from '../../utils/appUtils';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {updateApplicationChecklist} from "../../Data/ApplicationData";
import {getEmployeeName} from "../../utils/utils";
import ErrorDialog from "../Dialogs/ErrorDialog";
import {useDialog} from "../../providers/DialogProvider";
import {IApplicationChecklist} from "../../Interfaces/Application/IApplicationChecklist";
import {IApplicationChecklistItem} from "../../Interfaces/Application/IApplicationChecklistItem";
import {IChecklistItemStatus} from "../../Interfaces/Common/IChecklistItemStatus";
import {Constants} from "../../utils/constants";
import {Theme} from "@mui/material/styles";
import {fetchData} from "../../Data/fetch";
import HistoryIcon from '@mui/icons-material/History';
import ApplicationChecklistItemHistoryDialog from "../Dialogs/ApplicationChecklistItemHistoryDialog";
import ApplicationChecklistHistoryDialog
  from "../Dialogs/ApplicationChecklistHistoryDialog/ApplicationChecklistHistoryDialog";
import {useEmployeeRolePermissions} from "../../Data/EmployeeRoleData";
import {IApplicationRowChecklist} from "../../Interfaces/Application/IApplicationRowChecklist";
import {TextArea, TextAreaChangeEvent} from "@progress/kendo-react-inputs";
import debounce from 'lodash.debounce';
import { ILookupItems } from '../../Interfaces/ILookupItems';
import { useBusinessRulesEngine } from '../../Data/BusinessRulesData';
import { hasValue } from "@amsanalytics/react-shared-library";

const checklistColors = {
  // Not all are used, but the below colors are matched on lightness
  green: '#E2FCE6', //light green
  red: '#FFDBDB', //light red
  orange: '#FFEEE2', //light orange
  purple: '#E3E3FF', //light purple
  blue: '#DFF2FD', //light blue
  gray: '#E6E6E6', //light gray
  white: '#FFFFFF'
}

export interface ApplicationChecklistSideBarProps {
  sideBarWidth?: number;
}

export default function ApplicationChecklistSideBar(props: ApplicationChecklistSideBarProps) {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const applicationChecklists = currentApp.applicationChecklists;
  const lookups = useSelector((state: AppState) => state.applications.lookups);

  const getApplicationsChecklistsToShow = (): IApplicationChecklist[] => {
    const applicationChecklistsToShow: IApplicationChecklist[] = [];
    
    lookups.checklists.forEach(c => {
      const matchedApplicationChecklist = applicationChecklists.find(ac => ac.checklistId === c.id);

      if(!matchedApplicationChecklist) {
        return;
      }

      if(c.applicationStatusesToShowInList.length === 0) {
        applicationChecklistsToShow.push(matchedApplicationChecklist);
      }

      if(c.applicationStatusesToShowInList.map(appStatus => appStatus.toUpperCase()).includes(currentApp.appStatusId.toUpperCase())) {
          applicationChecklistsToShow.push(matchedApplicationChecklist);
      };
    })

    return applicationChecklistsToShow;
  }

  const applicationChecklistsToShow = getApplicationsChecklistsToShow();
  
  
  return (
    <div style={{display: 'flex', flexDirection: 'column', width: `${props.sideBarWidth ?? 0}px`}}>
      {applicationChecklistsToShow &&
        applicationChecklistsToShow.map((ac, key) => {
          return (
            <ApplicationChecklistAccordion
              key={key}
              applicationChecklist={ac}
              lookups={lookups}
            />
          )
        })  
      }
    </div>
  );
}

const getApplicationChecklistDeepCopy = (oldApplicationChecklist: IApplicationChecklist | undefined) => {
  let updatedApplicationChecklist: IApplicationChecklist | undefined = undefined;

  if (oldApplicationChecklist) {
    updatedApplicationChecklist = {
      ...oldApplicationChecklist,
      applicationChecklistItems: oldApplicationChecklist.applicationChecklistItems.map(aci => {
        return {
          ...aci
        };
      })
    };
  }

  return updatedApplicationChecklist;
};

export interface ApplicationChecklistAccordionProps {
  applicationChecklist: IApplicationChecklist;
  lookups: ILookupItems;
}

function ApplicationChecklistAccordion(props: ApplicationChecklistAccordionProps) {
  const [applicationChecklist, setApplicationChecklist] = useState<IApplicationChecklist | undefined>(
    getApplicationChecklistDeepCopy(props.applicationChecklist) ?? undefined
  );
  const initialApplicationChecklist = getApplicationChecklistDeepCopy(props.applicationChecklist);
  const checklist = props.lookups.checklists.find(c => c.id === applicationChecklist?.checklistId);
  const activeUser: IEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const dispatch = useDispatch<ApplicationDispatch>();
  const [edited, setEdited] = useState(false);
  const [openDialog, closeDialog] = useDialog();
  const [canView, canEdit, canAct] = useEmployeeRolePermissions();
  const employees: IEmployee[] = useSelector((state: AppState) => state.applications.employees);
  const approvedEmployee = employees.find(e => e.id === applicationChecklist?.approvedBy);
  const areChecklistItemsComplete = getNumChecklistItemsFinished(applicationChecklist!) == applicationChecklist?.applicationChecklistItems.length;
  const isReadyToApprove = areChecklistItemsComplete && !applicationChecklist?.approvedDate;
  const [clearChecklistItem, setClearChecklistItem] = useState({});
  const businessRulesEngine = useBusinessRulesEngine();
  const editingApp = useSelector((state: AppState) => state.applications.editingApp);
  const handleSave = async (appChecklist: IApplicationChecklist) => {
    if (applicationChecklist) {
      await updateApplicationChecklist(
        editingApp!,
        appChecklist,
        businessRulesEngine,
        dispatch
      );
    }
    setEdited(false);
  }

  const handleChecklistItemStatusChange = (id: string, newStatusId: string) => {
    setEdited(true);
    const updatedApplicationChecklist = getApplicationChecklistDeepCopy(applicationChecklist);

    if (updatedApplicationChecklist) {
      const updatedApplicationChecklistItem = updatedApplicationChecklist.applicationChecklistItems.find(aci => aci.id === id);

      if (updatedApplicationChecklistItem) {
        updatedApplicationChecklistItem.checklistItemStatusId = newStatusId;
        updatedApplicationChecklistItem.modifiedBy = activeUser.id;
        updatedApplicationChecklistItem.modifiedDate = new Date();
      }

      setApplicationChecklist(updatedApplicationChecklist);
    }
  };

  const handleChecklistItemCommentChange = (id: string, newComment: string) => {
    setEdited(true);
    const updatedApplicationChecklist = getApplicationChecklistDeepCopy(applicationChecklist);
    
    if (updatedApplicationChecklist) {
      const updatedApplicationChecklistItem = updatedApplicationChecklist.applicationChecklistItems.find(aci => aci.id === id);

      if (updatedApplicationChecklistItem) {
        updatedApplicationChecklistItem.comment = newComment;
        updatedApplicationChecklistItem.modifiedBy = activeUser.id;
        updatedApplicationChecklistItem.modifiedDate = new Date();
      }

      setApplicationChecklist(updatedApplicationChecklist);
    }
  };

  const handleApproveChecklist = () => {
    setEdited(true);
    const updatedApplicationChecklist = getApplicationChecklistDeepCopy(applicationChecklist);
    
    if (updatedApplicationChecklist) {
      updatedApplicationChecklist.approvedDate = new Date();
      updatedApplicationChecklist.approvedBy = activeUser.id;
      setApplicationChecklist(updatedApplicationChecklist);
      
      handleSave(updatedApplicationChecklist);
    }
  };
  
  const handleUnapproveChecklist = () => {
    setEdited(true);
    const updatedApplicationChecklist = getApplicationChecklistDeepCopy(applicationChecklist);

    if (updatedApplicationChecklist) {
      updatedApplicationChecklist.approvedDate = undefined;
      updatedApplicationChecklist.approvedBy = "";
      updatedApplicationChecklist.approversSignature = "";
      setApplicationChecklist(updatedApplicationChecklist);
    }
  };

  const handleChecklistCommentChange = (newChecklistComment: string) => {
    setEdited(true);
    const updatedApplicationChecklist = getApplicationChecklistDeepCopy(applicationChecklist);
    
    if (updatedApplicationChecklist) {
      updatedApplicationChecklist.checklistComment = newChecklistComment;
      setApplicationChecklist(updatedApplicationChecklist);
    }
  };

  const handleChecklistApproversSignature = (newApproversSignature: string) => {
    setEdited(true);
    const updatedApplicationChecklist = getApplicationChecklistDeepCopy(applicationChecklist);
    
    if (updatedApplicationChecklist) {
      updatedApplicationChecklist.approversSignature = newApproversSignature;
      setApplicationChecklist(updatedApplicationChecklist);
    }
  };

  const validateChecklist = (appChecklist: IApplicationChecklist): string[] => {
    const errors: string[] = [];

    if(!hasValue(appChecklist.approversSignature)) {
      errors.push('You must type in your name to sign the checklist');
    }

    return errors;
  };
  
  const numberOfFinishedItems = getNumChecklistItemsFinished(applicationChecklist!);
  const numberOfTotalItems = applicationChecklist!.applicationChecklistItems.length;
  
  return (
    <>
      {applicationChecklist &&
        canViewChecklist(activeUser, applicationChecklist.checklistId, props.lookups.employeeRoleChecklists) &&
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{
              backgroundColor: `${
                !!applicationChecklist?.approvedDate 
                  ? checklistColors.green //light green
                  : edited 
                    ? checklistColors.red //light red
                    : checklistColors.gray //light gray
              }`
            }}
          >
            <Typography style={{display: 'flex', fontSize: 15}}>
              {checklist?.name} Checklist- {getFinishedChecklistItemsString(numberOfFinishedItems, numberOfTotalItems, applicationChecklist.approvedDate)} {edited ? '(Edited)' : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{overflowY: "auto", flexDirection: 'column'}}>
            <div style={{alignItems: 'center', display: 'flex', flex: 1}}>
              <div style={{display: 'flex'}}>
                {!applicationChecklist.approvedDate &&
                  <div style={{display: 'flex'}}>
                    <Button
                      size={"small"}
                      style={{marginRight: '10px'}}
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSave(applicationChecklist);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      size={"small"}
                      style={{marginRight: '10px'}}
                      variant="contained"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setApplicationChecklist(initialApplicationChecklist);
                        setEdited(false);
                        setClearChecklistItem({});
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                }
                {applicationChecklist.approvedDate
                  ?
                    <>
                      <Typography style={{fontSize: 15}}>
                        Approved by {approvedEmployee?.firstName} {approvedEmployee?.lastName} on {new Date(applicationChecklist.approvedDate).toLocaleString()}
                      </Typography>
                      {canAct("Actions", "Edit Approved Checklists") &&
                        <Button
                          size={"small"}
                          style={{marginRight: '10px', width: '130px'}}
                          variant="contained"
                          color="error"
                          onClick={handleUnapproveChecklist}
                        >
                          Unapprove
                        </Button>
                      }
                    </>
                  :
                    <Button
                      size={"small"}
                      style={{marginRight: '10px'}}
                      variant="contained"
                      color="error"
                      disabled={!isReadyToApprove}
                      onClick={() => {
                        const checklistErrors = validateChecklist(applicationChecklist);
                        if (checklistErrors.length > 0) {
                          openDialog({
                            children: (
                              <ErrorDialog
                                title="Reference Validation Errors"
                                errors={checklistErrors}
                                closeDialog={closeDialog}
                              />
                            ),
                          });
                        } else {
                          handleApproveChecklist();
                        }
                      }}
                    >
                      Approve
                    </Button>
                }
                <TextField
                  value={applicationChecklist.approversSignature}
                  onChange={(e) => {
                    handleChecklistApproversSignature(e.target.value)
                  }}
                  variant="outlined"
                  label="Approver's Signature"
                  size="small"
                  disabled={!isReadyToApprove}
                  style={{display: 'flex', marginTop: '5px'}}
                />
                <IconButton
                  onClick={async () => {
                    const url = `api/ApplicationChecklist/getApplicationChecklistHistory/${applicationChecklist.id}`
                    const applicationChecklistHistories = await fetchData(url, dispatch);
                    openDialog({
                      fullScreen: false,
                      children: 
                        <ApplicationChecklistHistoryDialog
                          applicationChecklistHistories={applicationChecklistHistories}
                          employees={employees}
                          checklistName={checklist?.name ?? ""}
                          closeDialog={closeDialog}
                        />
                    });
                  }}
                >
                  <HistoryIcon fontSize="small"/>
                </IconButton>
              </div>
            </div>
            <TextField
              margin="dense"
              id="checklist-item-text"
              label="Checklist Note"
              disabled={!!applicationChecklist.approvedDate}
              value={applicationChecklist.checklistComment}
              onChange={(e) =>
                handleChecklistCommentChange(e.currentTarget.value)
              }
              fullWidth
              multiline
              style={{marginBottom: 10}}
            />
            {props.lookups.checklistItems?.map((ci, key) => {
                const aci = applicationChecklist?.applicationChecklistItems.find(aci => aci.checklistItemId === ci.id);
                if (aci) {
                  return (
                    <ApplicationChecklistItemComponent
                      key={key}
                      clear={clearChecklistItem}
                      applicationChecklistItem={aci}
                      checklistItemDescription={ci.description}
                      employees={employees}
                      checklistItemStatuses={props.lookups.checklistItemStatuses}
                      handleChecklistItemStatusChange={handleChecklistItemStatusChange}
                      handleChecklistItemCommentChange={handleChecklistItemCommentChange}
                      disabled={!!applicationChecklist.approvedDate}
                      showHistoryButton={true}
                    />
                  )
                }
              }
            )}
          </AccordionDetails>
        </Accordion>
      }
    </>
  );
}

// Used so that we can only update the parent's state whenever they're done typing
const checklistItemCommentUpdateDebounce = debounce((e: TextAreaChangeEvent, handleChecklistItemCommentChange: (id: string, value: string) => void, id: string) => {
  handleChecklistItemCommentChange(id, e.value);
}, 100);

export interface ApplicationChecklistItemComponentProps {
  applicationChecklistItem: IApplicationChecklistItem;
  checklistItemDescription?: string;
  checklistItemStatuses: IChecklistItemStatus[];
  employees: IEmployee[];
  handleChecklistItemStatusChange?: (id: string, newStatusId: string) => void;
  handleChecklistItemCommentChange?: (id: string, newComment: string) => void;
  showHistoryButton: boolean;
  disabled: boolean;
  clear: any; // used to reset the local "checklistItemComment" state variable
}

export function ApplicationChecklistItemComponent(props: ApplicationChecklistItemComponentProps) {
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch<ApplicationDispatch>();
  const editedBy = getEmployeeName(props.employees, props.applicationChecklistItem?.modifiedBy);
  // Used so that we can update comment on the screen in real time
  const [checklistItemComment, setChecklistItemComment] = useState<string>(props.applicationChecklistItem.comment);
  
  useEffect(() => {
    setChecklistItemComment(props.applicationChecklistItem.comment);
  }, [props.clear]);
  
  const getColorForStatus = (statusId: string): string | undefined => {
    switch(statusId) {
      case Constants.checklistItemStatuses.notStartedId:
        return undefined;
      case Constants.checklistItemStatuses.inProgressId:
        return checklistColors.blue;
      case Constants.checklistItemStatuses.completedId:
        return checklistColors.green;
      case Constants.checklistItemStatuses.exceptionId:
        return checklistColors.orange;
      case Constants.checklistItemStatuses.problemId:
        return checklistColors.red;
      default:
        return undefined;
    }
  }

  return (props.applicationChecklistItem &&
    <>
      <Divider variant="fullWidth"/>
      <div style={{display: 'flex', flex: 1}}>
        <div style={{display: 'flex', flexDirection: 'column', width: "150px", alignItems: 'center'}}>          
          <TextField
            fullWidth={true}
            select
            style={{
              flex: 1,
              marginTop: "10px",
              alignItems: 'center',
              maxHeight: "40px",
              backgroundColor: getColorForStatus(props.applicationChecklistItem.checklistItemStatusId)
            }}
            value={props.applicationChecklistItem.checklistItemStatusId}
            onChange={(e) => {
              props.handleChecklistItemStatusChange && props.handleChecklistItemStatusChange(props.applicationChecklistItem?.id, e.target.value);
            }}
            variant="outlined"
            label="Status"
            InputLabelProps={{style:{backgroundColor: "white"}}}
            size="small"
            disabled={props.disabled}
          >
            {props.checklistItemStatuses.map((cis, key) =>
              <MenuItem key={key} value={cis.id}>{cis.name}</MenuItem>
            )}
          </TextField>
          <Typography style={{flex: 1, fontSize: 12, alignItems: 'center', marginLeft: "10px"}}>
            {
              editedBy !== "Unknown"
                ? `Last edited by ${editedBy} ` +
                  `on ${new Date(props.applicationChecklistItem.modifiedDate).toLocaleString()}`
                : ''
            }
          </Typography>
          {props.showHistoryButton &&
            <IconButton
              onClick={async () => {
                const url = `api/ApplicationChecklist/getApplicationChecklistItemHistory/${props.applicationChecklistItem.id}`
                const applicationChecklistItemHistories = await fetchData(url, dispatch);
                openDialog({
                  fullScreen: false,
                  children:
                    <ApplicationChecklistItemHistoryDialog
                      checklistItemDescription={props.checklistItemDescription}
                      applicationChecklistItemHistories={applicationChecklistItemHistories}
                      checklistItemStatuses={props.checklistItemStatuses}
                      employees={props.employees}
                      closeDialog={closeDialog}
                    />
                });
              }}
            >
              <HistoryIcon fontSize="small"/>
            </IconButton>
          }
        </div>
        <div style={{display: 'flex', flex: 1, flexDirection: 'column', marginLeft: 10}}>
          <Typography style={{display: 'flex', alignItems: 'bottom', fontSize: 14}}>
            {props.checklistItemDescription}
          </Typography>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <TextArea
              id="checklist-item-text"
              value={checklistItemComment}
              disabled={props.disabled}
              onChange={(e) => {
                setChecklistItemComment(e.value);
                checklistItemCommentUpdateDebounce(e, props.handleChecklistItemCommentChange!, props.applicationChecklistItem.id);
              }}
              style={{width: "100%"}}
              placeholder={"Comment"}
            />
          </div>
        </div>
      </div>
    </>
  );
}


export interface ChecklistButtonToolTipTextProps {
  applicationChecklists: IApplicationChecklist[];
  lookups: ILookupItems
}

export function ChecklistButtonToolTipText(props: ChecklistButtonToolTipTextProps) {
  const unexcludedChecklists = getUnexcludedApplicationChecklists(props.lookups.checklists, props.applicationChecklists);
  
  return (
    <>
      <Typography style={{fontSize: 12}}>
        Checklists-
      </Typography>
      {unexcludedChecklists?.map((ac, key) => {
        const numberOfFinishedItems = getNumChecklistItemsFinished(ac);
        const numberOfTotalItems = ac.applicationChecklistItems.length;
          
        return (
          <div key={key}>
            <Typography style={{fontSize: 12, marginLeft: '15px'}}>
              {key + 1}. {props.lookups.checklists.find(c => c.id === ac.checklistId)?.name}
            </Typography>
            <Typography style={{fontSize: 12, marginLeft: '30px'}}>
              {getFinishedChecklistItemsString(numberOfFinishedItems, numberOfTotalItems, ac.approvedDate)}
            </Typography>
            {ac.checklistComment &&
            <Typography style={{fontSize: 12, marginLeft: '30px'}}>
              Note- {ac.checklistComment}
            </Typography>
            }
          </div>
        );
      })}
    </>
  );
}

//This is the component for the ApplicationRows in the queues
export interface ChecklistRowButtonToolTipTextProps {
  applicationRowChecklists: IApplicationRowChecklist[];
  lookups: ILookupItems
}

export function ChecklistRowButtonToolTipText(props: ChecklistRowButtonToolTipTextProps) {
  return (
    <>
      <Typography style={{fontSize: 12}}>
        Checklists-
      </Typography>
      {props.applicationRowChecklists.map((arc, key) => {
        const numberOfFinishedItems = arc.numberOfFinishedItems;
        const numberOfTotalItems = arc.numberOfTotalItems;

        return (
          <div key={key}>
            <Typography style={{fontSize: 12, marginLeft: '15px'}}>
              {key + 1}. {props.lookups.checklists.find(c => c.id === arc.checklistId)?.name}
            </Typography>
            <Typography style={{fontSize: 12, marginLeft: '30px'}}>
              {getFinishedChecklistItemsString(numberOfFinishedItems, numberOfTotalItems, arc.approvedDate)}
            </Typography>
            {arc.checklistComment &&
              <Typography style={{fontSize: 12, marginLeft: '30px'}}>
                Note- {arc.checklistComment}
              </Typography>
            }
          </div>
        );
      })}
    </>
  );
}